import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Expand, ArrowUp, ArrowLeft } from "lucide-react";
import { TOPICS, CONTACT, MAKER } from "../data/content";

const LegalModal = ({ open, onClose }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        data-testid="legal-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={onClose}
        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 px-4 py-8"
      >
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 20, opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="relative max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-md bg-swede-cream p-8 shadow-2xl md:p-10"
        >
          <button
            type="button"
            data-testid="legal-modal-close"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-black/20 text-black transition-colors hover:bg-black/5"
          >
            <X size={18} />
          </button>
          <h3 className="font-accent text-2xl tracking-wide text-black md:text-3xl">Terms &amp; Privacy</h3>
          <p className="mt-1 text-xs text-black/60">Last updated February 2026</p>

          <div className="mt-6 space-y-4 font-body text-sm leading-relaxed text-black/80 md:text-base">
            <p>
              <span className="font-semibold text-black">About this site.</span> Swede&rsquo;s Wood is a small,
              owner-operated workshop in Overland Park, Kansas. This website is a portfolio showcasing custom wood
              and metal work. It is provided for informational purposes only. Nothing here is a binding offer or a
              warranty of any specific piece &mdash; each project is discussed and quoted directly with the customer.
            </p>

            <p>
              <span className="font-semibold text-black">Photos &amp; content.</span> All photographs, descriptions,
              and artwork shown on this site are private property and are shared here purely for
              display. Please don&rsquo;t reproduce, resell, or repost them without permission. Each piece is
              handmade, so wood grain, color, and dimensions will vary from what you see in the photos.
            </p>

            <p>
              <span className="font-semibold text-black">Privacy.</span> We do not collect personal information from
              visitors to this site. There are no accounts, no tracking cookies, and no analytics beyond what the
              hosting provider may log for basic security. If you email us using the contact link, we&rsquo;ll only
              use your message to reply to you &mdash; we don&rsquo;t share, sell, or add you to any list. Photos on
              this site have been stripped of location and camera metadata before being posted.
            </p>

            <p>
              <span className="font-semibold text-black">Getting in touch.</span> Ready to talk about a project, or
              have a question? Just email us at{" "}
              <a href={`mailto:${CONTACT.email}`} className="underline">
                {CONTACT.email}
              </a>
              . We&rsquo;ll get back to you as soon as the sawdust settles.
            </p>

            <p className="text-xs text-black/60">
              &copy; {new Date().getFullYear()} Swede&rsquo;s Wood. All rights reserved.
            </p>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

const Photo = ({ photo, fit, index, onOpen }) => (
  <motion.button
    type="button"
    initial={{ opacity: 0, y: 34 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.8, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
    data-testid="gallery-photo"
    onClick={() => onOpen(photo)}
    aria-label={`Enlarge photo: ${photo.alt}`}
    className="group relative block w-full cursor-zoom-in overflow-hidden rounded-md bg-white p-3 text-left shadow-[0_16px_40px_-18px_rgba(28,28,28,0.45)] ring-1 ring-black/5 transition-shadow duration-500 hover:shadow-[0_26px_60px_-20px_rgba(28,28,28,0.55)]"
  >
    <div className="relative overflow-hidden rounded-sm bg-[#f0ece3]">
      <img
        src={photo.src}
        alt={photo.alt}
        loading="lazy"
        className={`h-72 w-full ${fit === "contain" ? "object-contain" : "object-cover"} transition-transform duration-[900ms] ease-out ${fit === "contain" ? "" : "group-hover:scale-[1.05]"}`}
      />
      <span className="pointer-events-none absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/45 text-white opacity-0 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100">
        <Expand size={16} />
      </span>
    </div>
    <span className="mt-3 flex items-center justify-between px-1">
      <span className="font-body text-base font-semibold text-black">{photo.alt}</span>
      <span className="overline text-sm font-semibold text-black">{photo.time}</span>
    </span>
  </motion.button>
);

const TopicSection = ({ topic, first, onOpen }) => (
  <section
    id={topic.id}
    data-testid={`topic-${topic.id}`}
    className={`scroll-mt-3 border-b border-black/10 px-5 pt-6 pb-10 md:px-10 md:pt-8 md:pb-14 md:scroll-mt-4 ${first ? "pt-10 md:pt-12" : ""}`}
  >
    <div className="mx-auto max-w-[1300px]">
      <div className="mb-5 max-w-3xl">
        <div className="mb-2 flex items-center gap-4">
          <span className="h-[3px] w-12 bg-swede-red" />
          <h2 data-testid={`topic-title-${topic.id}`} className="font-display text-3xl font-semibold leading-none text-black md:text-5xl">
            {topic.label}
          </h2>
        </div>
        <div className="space-y-3">
          {topic.paragraphs.map((p, i) => (
            <p key={i} className="font-body text-sm leading-relaxed text-black/70 md:text-base">
              {p}
            </p>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {topic.photos.map((photo, i) => (
          <Photo key={photo.src} photo={photo} fit={topic.fit} index={i} onOpen={onOpen} />
        ))}
      </div>
    </div>
  </section>
);

const MakerSection = ({ onOpen }) => (
  <section
    id={MAKER.id}
    data-testid={`topic-${MAKER.id}`}
    className="scroll-mt-4 border-t-2 border-swede-red/70 bg-swede-sand px-5 py-10 md:px-10 md:py-14 md:scroll-mt-6"
  >
    <div className="mx-auto grid max-w-[1300px] grid-cols-1 items-center gap-10 md:grid-cols-12 md:gap-14">
      <motion.div
        initial={{ opacity: 0, y: 34 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="md:col-span-5"
      >
        <button
          type="button"
          onClick={() => onOpen({ src: MAKER.photo, alt: MAKER.name })}
          aria-label={`Enlarge photo: ${MAKER.name}`}
          className="group mx-auto block max-w-sm cursor-zoom-in rounded-md bg-white p-3 shadow-[0_16px_40px_-18px_rgba(28,28,28,0.45)] ring-1 ring-black/5"
        >
          <div className="relative overflow-hidden rounded-sm">
            <img
              src={MAKER.photo}
              alt={MAKER.name}
              data-testid="maker-photo"
              className="h-[520px] w-full rounded-sm object-contain"
            />
            <span className="pointer-events-none absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/45 text-white opacity-0 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100">
              <Expand size={16} />
            </span>
          </div>
        </button>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 34 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
        className="md:col-span-7"
      >
        <p className="overline mb-4 text-swede-red">Meet the Maker</p>
        <h2 data-testid="maker-name" className="font-display text-4xl font-semibold leading-none text-black md:text-6xl">
          {MAKER.name}
        </h2>
        <div className="mt-6 space-y-4">
          {MAKER.bio.map((p, i) => (
            <p key={i} className="font-body text-base leading-relaxed text-black/70 md:text-lg">{p}</p>
          ))}
        </div>

        <button
          type="button"
          onClick={() => onOpen({ src: MAKER.signPhoto, alt: "There is always, always, always something to be thankful for!" })}
          aria-label="Enlarge photo: a thankful reminder"
          className="group mt-8 flex max-w-xs cursor-zoom-in items-center gap-4 rounded-md bg-white p-2.5 shadow-[0_14px_34px_-18px_rgba(28,28,28,0.45)] ring-1 ring-black/5"
        >
          <img
            src={MAKER.signPhoto}
            alt="Hand-burned pallet sign reading: there is always something to be thankful for"
            className="h-24 w-24 rounded-sm object-cover"
          />
          <span className="pr-2 text-left font-display text-lg italic leading-snug text-black/70">
            &ldquo;There is always something to be thankful for.&rdquo;
          </span>
        </button>
      </motion.div>
    </div>
  </section>
);

const Lightbox = ({ photo, onClose }) => (
  <motion.div
    data-testid="lightbox-overlay"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.25 }}
    onClick={onClose}
    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm md:p-10"
  >
    <button
      type="button"
      data-testid="lightbox-close"
      onClick={onClose}
      aria-label="Close"
      className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full border border-white/30 text-white transition-colors hover:bg-white/10"
    >
      <X size={22} />
    </button>
    <motion.img
      key={photo.src}
      data-testid="lightbox-image"
      src={photo.src}
      alt={photo.alt}
      initial={{ scale: 0.94, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.96, opacity: 0 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      onClick={(e) => e.stopPropagation()}
      className="h-full w-full rounded-md object-contain shadow-2xl"
    />
  </motion.div>
);

export const Collection = () => {
  const { hash } = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useState(null);
  const [legalOpen, setLegalOpen] = useState(false);
  const [showTop, setShowTop] = useState(false);

  const openPhoto = useCallback((photo) => setActive(photo), []);
  const closePhoto = useCallback(() => setActive(null), []);

  // Show the floating "back to top" arrow once the visitor is past the
  // first category (Storm-Sourced). We use its bottom edge as the trigger.
  useEffect(() => {
    const firstId = TOPICS[0].id;
    const onScroll = () => {
      const el = document.getElementById(firstId);
      if (!el) return;
      const bottom = el.getBoundingClientRect().bottom;
      setShowTop(bottom < 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const goCover = () => navigate("/");

  useEffect(() => {
    const id = hash ? hash.slice(1) : null;
    // First category (Storm-Sourced) or no hash -> land at very top so the
    // master site header is fully visible.
    if (!id || id === TOPICS[0].id) {
      window.scrollTo(0, 0);
      return;
    }
    // Other categories -> instant jump so the section title sits near the
    // top edge (~10mm clearance handled by scroll-mt on the section).
    let cancelled = false;
    const jump = () => {
      const el = document.getElementById(id);
      if (!el) return;
      const y = el.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: y, behavior: "auto" });
    };
    const t1 = setTimeout(() => !cancelled && jump(), 40);
    const t2 = setTimeout(() => !cancelled && jump(), 400);
    return () => {
      cancelled = true;
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [hash]);

  // Escape to close + lock scroll while open
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => e.key === "Escape" && closePhoto();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [active, closePhoto]);

  return (
    <div data-testid="collection-page" className="min-h-screen bg-swede-cream">
      <header data-testid="collection-header" className="bg-swede-cream px-5 pt-8 pb-3 text-center md:pt-10">
        <h1 className="font-accent text-4xl leading-none tracking-wide text-swede-forest md:text-6xl">
          Swede&rsquo;s Wood
        </h1>
        <p className="overline mt-3 text-sm font-bold text-black md:text-base">Handcrafted Woodworking</p>
        <p className="overline mt-1 text-sm font-bold text-black md:text-base">Custom BandSaw &amp; ReSaw Milling</p>
        <div className="mx-auto mt-4 h-[3px] w-16 bg-swede-tan" />
      </header>
      <main>
        {TOPICS.map((topic, i) => (
          <TopicSection key={topic.id} topic={topic} first={i === 0} onOpen={openPhoto} />
        ))}
        <MakerSection onOpen={openPhoto} />
      </main>

      {/* Single-line contact bar */}
      <footer data-testid="contact-bar" className="bg-swede-cream px-5 py-10 text-center">
        <p className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 font-body text-sm text-black md:text-base">
          <span data-testid="contact-name" className="font-semibold">{CONTACT.name}</span>
          <span className="text-black/30">&middot;</span>
          <a data-testid="contact-email" href={`mailto:${CONTACT.email}`} className="link-sweep">{CONTACT.email}</a>
          {CONTACT.location && (
            <>
              <span className="text-black/30">&middot;</span>
              <span data-testid="contact-location">{CONTACT.location}</span>
            </>
          )}
          {CONTACT.phone && (
            <>
              <span className="text-black/30">&middot;</span>
              <a data-testid="contact-phone" href={`tel:${CONTACT.phone.replace(/[^0-9+]/g, "")}`} className="link-sweep">{CONTACT.phone}</a>
            </>
          )}
        </p>
        <p className="mt-3 text-xs text-black/55">
          &copy; {new Date().getFullYear()} Swede&rsquo;s Wood &middot;{" "}
          <button
            type="button"
            data-testid="open-legal"
            onClick={() => setLegalOpen(true)}
            className="underline underline-offset-2 transition-colors hover:text-black"
          >
            Terms &amp; Privacy
          </button>
        </p>
      </footer>

      <LegalModal open={legalOpen} onClose={() => setLegalOpen(false)} />

      <AnimatePresence>
        {showTop && (
          <motion.div
            data-testid="floating-nav"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-6 right-6 z-40 flex flex-col items-stretch gap-2 md:bottom-8 md:right-8"
          >
            <button
              type="button"
              data-testid="back-to-top"
              onClick={scrollToTop}
              aria-label="Back to top of page"
              className="flex items-center justify-center gap-2 rounded-full bg-swede-forest px-4 py-2 font-body text-sm font-semibold text-swede-cream shadow-lg ring-1 ring-black/10 transition-colors hover:bg-black"
            >
              <ArrowUp size={16} />
              <span>Top</span>
            </button>
            <button
              type="button"
              data-testid="back-to-cover"
              onClick={goCover}
              aria-label="Back to cover page"
              className="flex items-center justify-center gap-2 rounded-full bg-swede-forest px-4 py-2 font-body text-sm font-semibold text-swede-cream shadow-lg ring-1 ring-black/10 transition-colors hover:bg-black"
            >
              <ArrowLeft size={16} />
              <span>Back</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {active && <Lightbox photo={active} onClose={closePhoto} />}
      </AnimatePresence>
    </div>
  );
};

export default Collection;
